import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trophy, Activity, CheckCircle, Users } from 'lucide-react';
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip } from 'recharts';
import { adminAPI } from '../services/api';
import StatCard from '../components/StatCard';
import { Card, CardHeader, CardTitle, CardContent } from '../components/Card';
import LoadingSpinner from '../components/LoadingSpinner';

const Tournaments = () => {
  const { data, isLoading } = useQuery({
    queryKey: ['tournamentStats'],
    queryFn: adminAPI.getTournamentStats,
  });

  if (isLoading) {
    return <LoadingSpinner text="Loading tournament statistics..." />;
  }

  const typeData = data?.byType
    ? Object.entries(data.byType).map(([name, value]) => ({
        name: name.charAt(0).toUpperCase() + name.slice(1),
        value,
      }))
    : [];

  const COLORS = ['#FF5E9F', '#FFB101', '#00D2D3'];

  return (
    <div className="space-y-4 sm:space-y-6">
      {/* Page Header */}
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-text-primary">Tournaments</h1>
        <p className="text-sm sm:text-base text-text-secondary mt-1">Overview of tournament statistics</p>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        <StatCard
          title="Total Tournaments"
          value={data?.total || 0}
          icon={Trophy}
          gradient={true}
        />
        <StatCard
          title="Active"
          value={data?.active || 0}
          icon={Activity}
          subtitle="Currently ongoing"
        />
        <StatCard
          title="Completed"
          value={data?.completed || 0}
          icon={CheckCircle}
        />
        <StatCard
          title="Avg Participants"
          value={data?.avgParticipants || 0}
          icon={Users}
        />
      </div>

      {/* Tournament Types Chart */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card>
          <CardHeader>
            <CardTitle>Tournaments by Type</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={typeData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }) =>
                    `${name} ${(percent * 100).toFixed(0)}%`
                  }
                  outerRadius={100}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {typeData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                  ))}
                </Pie>
                <Tooltip
                  contentStyle={{
                    backgroundColor: '#1A1A1F',
                    border: '1px solid #555555',
                    borderRadius: '8px',
                    color: '#FFFFFF',
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Tournament Statistics</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-background-elevated rounded-lg">
                <div>
                  <p className="text-sm text-text-secondary">Completion Rate</p>
                  <p className="text-2xl font-bold text-text-primary mt-1">
                    {data?.total > 0
                      ? ((data.completed / data.total) * 100).toFixed(1)
                      : 0}
                    %
                  </p>
                </div>
                <CheckCircle size={32} className="text-success" />
              </div>

              <div className="flex items-center justify-between p-4 bg-background-elevated rounded-lg">
                <div>
                  <p className="text-sm text-text-secondary">Active Rate</p>
                  <p className="text-2xl font-bold text-text-primary mt-1">
                    {data?.total > 0 ? ((data.active / data.total) * 100).toFixed(1) : 0}%
                  </p>
                </div>
                <Activity size={32} className="text-warning" />
              </div>

              <div className="flex items-center justify-between p-4 bg-background-elevated rounded-lg">
                <div>
                  <p className="text-sm text-text-secondary">Average Participants</p>
                  <p className="text-2xl font-bold text-text-primary mt-1">
                    {data?.avgParticipants || 0}
                  </p>
                </div>
                <Users size={32} className="text-primary" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tournament Type Breakdown Table */}
      <Card>
        <CardHeader>
          <CardTitle>Tournament Types Breakdown</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-background-elevated border-b border-border">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Type
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Count
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-text-secondary uppercase">
                    Percentage
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {typeData.map((type, index) => (
                  <tr key={index} className="hover:bg-background-elevated transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center gap-2">
                        <div
                          className="w-3 h-3 rounded-full"
                          style={{ backgroundColor: COLORS[index % COLORS.length] }}
                        />
                        <span className="text-sm font-medium text-text-primary">
                          {type.name}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-text-primary">{type.value}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm font-medium text-text-primary">
                        {data?.total > 0
                          ? ((type.value / data.total) * 100).toFixed(1)
                          : 0}
                        %
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default Tournaments;
